import React from "react";
import { useDispatch, useSelector } from "react-redux";

// constants
import { counsellorCols, universityCols } from "./constants";
import { ACCESS_TYPE } from "@/store/actions/universityDetailsActions";

// components
import Pagination from "./pagination";
import { DesktopTable, MobileTable } from "./table";

// actions
import { reportsGetAndStore, downloadReports } from "@/store/actions/reportsActions";

// mui
import { Box, Grid, Typography, IconButton, CircularProgress, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import GetAppIcon from "@material-ui/icons/GetApp";

export default function UniversityTab() {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const universityDetails = useSelector((state) => state.universityDetails.data);
    const loadingReports = useSelector((state) => state.reports.loading);
    const totalStudents = useSelector((state) => state.reports.totalStudents);
    const downloading = useSelector((state) => state.reports.downloading);
    const data = useSelector((state) => state.reports.data);
    const total = useSelector((state) => state.reports.total);

    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const handleChangePage = (_, pageNumber) => {
        const newPage = pageNumber + 1;
        setPage(newPage);
        window.scrollTo({ top: 0 });
        dispatch(
            reportsGetAndStore({
                access: universityDetails.showPU ? ACCESS_TYPE.counsellor : ACCESS_TYPE.university,
                university_code: universityDetails.universityCode,
                university_name: universityDetails.universityName,
                fair_id: universityDetails.fairID,
                page: newPage,
                per_page: rowsPerPage,
            })
        );
    };

    const handleChangeRowsPerPage = (event) => {
        const newPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newPerPage);
        setPage(1);
        window.scrollTo({ top: 0 });
        dispatch(
            reportsGetAndStore({
                access: universityDetails.showPU ? ACCESS_TYPE.counsellor : ACCESS_TYPE.university,
                university_code: universityDetails.universityCode,
                university_name: universityDetails.universityName,
                fair_id: universityDetails.fairID,
                page: 1,
                per_page: newPerPage,
            })
        );
    };

    const handleDownloadReports = () => {
        dispatch(
            downloadReports(
                {
                    access: universityDetails.showPU ? ACCESS_TYPE.counsellor : ACCESS_TYPE.university,
                    university_code: universityDetails.universityCode,
                    university_name: universityDetails.universityName,
                    fair_id: universityDetails.fairID,
                },
                `Azent_${universityDetails.universityName.replace(/ /g, "_").toLowerCase()}`
            )
        );
    };

    const tableColsData = React.useMemo(
        () => (universityDetails.showPU ? counsellorCols : universityCols),
        [universityDetails]
    );

    return (
        <Box>
            <Grid container alignItems="center">
                <Grid item xs={11}>
                    <Typography variant="h2">
                        {totalStudents
                            ? `${universityDetails.universityName} (${totalStudents})`
                            : universityDetails.universityName}
                    </Typography>
                    {universityDetails.showPU && (
                        <Typography variant="subtitle1">
                            {
                                'Note : The count does not include students marked as an "internal team" and "university representative"'
                            }
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={1}>
                    <Box textAlign="right">
                        {downloading ? (
                            <Box height={"51.42px"} overflow="hidden" p={1.5}>
                                <CircularProgress size={"20px"} color="primary" />
                            </Box>
                        ) : (
                            <IconButton
                                size="medium"
                                onClick={handleDownloadReports}
                                disabled={loadingReports}
                            >
                                <GetAppIcon fontSize="medium" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {isDesktop ? (
                <DesktopTable colsData={tableColsData} data={data} loading={loadingReports} />
            ) : (
                <MobileTable colsData={tableColsData} data={data} loading={loadingReports} />
            )}
            <Pagination
                page={page}
                rowsPerPage={rowsPerPage}
                total={total}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                disabled={loadingReports}
            />
        </Box>
    );
}
